<template>
  <div class="main-container">
    <!-- Toast Notification -->
    <Toast />

    <!-- Sidebar, hidden if user is not authenticated -->
    <div
      v-if="isAuthenticated"
      :class="['sidebar-container', { hidden: !sidebarVisible }]"
    >
      <SidebarMenu
        :model="sidebarRoutes"
        :isVisible="sidebarVisible"
        @close-sidebar="toggleSidebar"
      />
    </div>

    <!-- Content area -->
    <div :class="['content-container', { 'full-width': !sidebarVisible }]">
      <!-- Top Menu, visible only when the user is authenticated -->
      <div class="top-menu-container" v-if="isAuthenticated">
        <TopMenu @menuClick="toggleSidebar" />
      </div>

      <!-- Scrollable Page Content -->
      <div class="content scrollable-content">
        <router-view></router-view>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, ref, onMounted, onUnmounted } from "vue";
import { useGlobalStore } from "@/stores/globalStore";
import { useRouter } from "vue-router";
import { Role } from "@/interfaces/Role";
import { SidebarMenu, TopMenu } from "@/components/designSystem";
import Toast from "primevue/toast";
import { useToast } from "primevue/usetoast";
import { notifyService } from "@/helpers/NotifyService";
import { useI18n } from "vue-i18n";

export default defineComponent({
  components: {
    SidebarMenu,
    TopMenu,
    Toast,
  },
  setup() {
    const userStore = useGlobalStore();
    const router = useRouter();
    const sidebarVisible = ref(true);
    const isTablet = ref(false);
    const toast = useToast();
    const { t } = useI18n();

    const toggleSidebar = () => {
      sidebarVisible.value = !sidebarVisible.value;
    };

    const checkIfTablet = () => {
      isTablet.value = window.innerWidth <= 840;
      sidebarVisible.value = !isTablet.value;
    };

    const isAuthenticated = computed(() => userStore.user.role !== Role.Guest);

    const sidebarRoutes = computed(() => {
      let userRole = userStore.user?.role;

      if (userRole === Role.ManagedUser || userRole === Role.StandaloneUser) {
        userRole = Role.Client;
      }
      const groups = {};
      router.getRoutes().forEach((route) => {
        if (route.meta && route.meta.role === userRole) {
          const groupKey = route.meta.group || "Other";
          const groupLabel = t(`navigation.groups.${groupKey}`);
          if (!groups[groupLabel]) {
            groups[groupLabel] = {
              label: groupLabel,
              items: [],
            };
          }
          groups[groupLabel].items.push({
            label: t(route.meta.label),
            icon: route.meta.icon || "pi pi-circle",
            route: route.path,
            order: route.meta.order || 0,
          });
        }
      });
      Object.values(groups).forEach((group) => {
        group.items.sort((a, b) => a.order - b.order);
      });
      return Object.values(groups);
    });

    onMounted(() => {
      checkIfTablet();
      window.addEventListener("resize", checkIfTablet);
    });

    onUnmounted(() => {
      window.removeEventListener("resize", checkIfTablet);
    });

    onMounted(() => {
      notifyService.setToastInstance(toast);
    });

    return {
      isAuthenticated,
      sidebarRoutes,
      sidebarVisible,
      toggleSidebar,
      isTablet,
    };
  },
});
</script>
